import {
	Button,
	Card,
	CardContent,
	CardHeader,
	Collapse,
	Grid,
	InputLabel,
	Stack,
} from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { IconArrowRight, IconEye } from '@tabler/icons';
import ExpandMore from 'components/cards/ExpandMore';
import MainCard from 'components/cards/MainCard';
import SkeletonEarningCard from 'components/cards/Skeleton/EarningCard';
import DateColumn from 'components/Table/DateColumn';
import EndUserEmailColumn from 'components/Table/EndUserEmailColumn';
import GenderColumn from 'components/Table/GenderColumn';
import PageTitle from 'components/Title/PageTitle';
import { IEndUser } from 'modules/EndUsersManagement/interfaces/EndUser';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { DashboardStateSelector } from '../store/slice';

const RecentUserCard: FC<{ user: IEndUser }> = ({ user }) => {
	const [expanded, setExpanded] = useState(false);

	const handleExpandClick = () => {
		setExpanded(!expanded);
	};

	return (
		<Card variant='outlined'>
			<CardHeader
				action={
					<>
						<Stack
							mt={1}
							justifyContent={'center'}
							alignItems={'center'}
							flexDirection={'row'}
						>
							<DateColumn withDayDate={false} date={user.createdAt} />
							<ExpandMore
								sx={{ ml: 1 }}
								expand={expanded}
								onClick={handleExpandClick}
								aria-expanded={expanded}
								aria-label='show more'
							>
								<GridExpandMoreIcon />
							</ExpandMore>
						</Stack>
					</>
				}
				titleTypographyProps={{
					maxWidth: '200px',
					className: 'ellipsis-text',
				}}
				subheaderTypographyProps={{
					maxWidth: '200px',
					className: 'ellipsis-text',
				}}
				title={user.name}
				subheader={user.email}
			/>
			<Collapse in={expanded} timeout='auto' unmountOnExit>
				<CardContent>
					<Grid container spacing={2}>
						<Grid item md={6} xs={12}>
							<InputLabel sx={{ mb: 1 }}>Email</InputLabel>
							<EndUserEmailColumn user={user} />
						</Grid>

						<Grid item md={6} xs={12}>
							<InputLabel sx={{ mb: 1 }}>Gender</InputLabel>
							<GenderColumn gender={user.gender} />
						</Grid>

						<Grid item md={6} xs={12}>
							<InputLabel sx={{ mb: 1 }}>Creation date</InputLabel>
							<DateColumn date={user.createdAt} />
						</Grid>

						<Grid item md={6} xs={12}>
							<InputLabel sx={{ mb: 1 }}>Last active date</InputLabel>
							<DateColumn date={user.updatedAt} />
						</Grid>

						<Grid item xs={12}>
							<Button
								fullWidth
								variant='outlined'
								size='small'
								startIcon={<IconEye />}
								to={`/end-users/${user.id}/profile`}
								component={Link}
							>
								See details
							</Button>
						</Grid>
					</Grid>
				</CardContent>
			</Collapse>
		</Card>
	);
};

const RecentUsers = () => {
	const { recentUsers, isLoading } = useSelector(DashboardStateSelector);
	if (isLoading)
		return (
			<>
				<SkeletonEarningCard />
				<SkeletonEarningCard />
				<SkeletonEarningCard />
			</>
		);
	return (
		<MainCard
			noPageTitle
			title={<PageTitle>Most recent users</PageTitle>}
			sx={{
				minHeight: '100%',
				maxHeight: 400,
				overflowY: 'auto',
				position: 'relative',
			}}
		>
			<Stack spacing={2}>
				{recentUsers.data.map(user => (
					<RecentUserCard key={user.id} user={user} />
				))}
			</Stack>
			<Stack
				flexDirection={'row'}
				justifyContent={'center'}
				alignContent={'stretch'}
				sx={{
					position: 'sticky',
					bottom: 0,
					width: '100%',
				}}
			>
				<Button
					variant='contained'
					size='large'
					endIcon={<IconArrowRight />}
					sx={{ my: 2 }}
					to={`/end-users`}
					component={Link}
				>
					See more
				</Button>
			</Stack>
		</MainCard>
	);
};

export default RecentUsers;
